import React, { useEffect, useState, useMemo } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import { Input, Spin, Empty } from 'antd';
import * as Icons from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { customThemes } from '../../../../themeConfig';

interface PICard {
  pi_id: number;
  pi_order_no: string;
  pi_date: string;
  pi_lead_time: number;
  ledger_company_name: string;
  ledger_first_name: string;
  ledger_last_name: string;
  items: { pid_product_id: number; pid_qty: number }[];
}

const getPriorityTheme = (pi_date: string, lead_time: number) => {
  const deadline = dayjs(pi_date).add(lead_time, 'day');
  const today = dayjs();
  const daysLeft = deadline.diff(today, 'day');

  if (daysLeft < 0) {
    // Overdue - Red priority
    return {
      color: '#ff4d4f',
      glow: 'rgba(255, 77, 79, 0.18)',
      bg: 'rgba(255, 77, 79, 0.08)',
      text: `${Math.abs(daysLeft)} days overdue`
    };
  }
  if (daysLeft <= 3) {
    // Due Soon - Yellow priority
    return {
      color: '#faad14',
      glow: 'rgba(250, 173, 20, 0.18)',
      bg: 'rgba(250, 173, 20, 0.08)',
      text: daysLeft === 0 ? 'Due today' : `${daysLeft} days left`
    };
  }
  // On Time - Green priority
  return {
    color: '#52c41a',
    glow: 'rgba(82, 196, 26, 0.18)',
    bg: 'rgba(82, 196, 26, 0.08)',
    text: `${daysLeft} days left`
  };
};

const IssuedWorkOrder: React.FC = () => {
  const [invoices, setInvoices] = useState<PICard[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Load context for light/dark theme variables
  const context = useOutletContext<any>() || {};
  const darkMode = context.darkMode || false;
  const baseTheme = darkMode ? customThemes.dark : customThemes.light;
  const themeStyles = { ...baseTheme, ...context.themeStyles };

  const customLayoutBg = darkMode ? '#0f172a' : '#f8fafc'; // Slate-900 vs Slate-50
  const customCardBg = darkMode ? '#1e293b' : '#ffffff';   // Slate-800 vs Pure White
  const customCardBorder = darkMode ? 'rgba(51, 65, 85, 0.45)' : 'rgba(226, 232, 240, 0.8)'; // Slate-700 vs Slate-200
  const customTextMain = darkMode ? '#f8fafc' : '#0f172a';  // Slate-50 vs Slate-900
  const customTextSub = darkMode ? '#94a3b8' : '#64748b';   // Slate-400 vs Slate-500

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const data = await apiRequest<PICard[]>({
        method: 'get',
        url: '/get-performa-invoices',
      });
      setInvoices(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Filter performa invoices in real-time
  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      const company = String(inv.ledger_company_name || '').toLowerCase();
      const firstName = String(inv.ledger_first_name || '').toLowerCase();
      const lastName = String(inv.ledger_last_name || '').toLowerCase();
      const orderNo = String(inv.pi_order_no || '').toLowerCase();
      const query = searchQuery.toLowerCase();

      return (
        company.includes(query) ||
        firstName.includes(query) ||
        lastName.includes(query) ||
        orderNo.includes(query)
      );
    });
  }, [invoices, searchQuery]);

  /* ================= INJECT DYNAMIC HOVER STYLES ================= */
  useEffect(() => {
    const styleId = "issued-work-order-cards-style";
    let styleTag = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }
    styleTag.innerHTML = `
      @keyframes cardSlideIn {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .work-order-bento-card {
        background: ${customCardBg} !important;
        border: 1px solid ${customCardBorder} !important;
        border-radius: 12px !important;
        padding: 24px !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: space-between !important;
        height: 160px !important;
        position: relative !important;
        overflow: hidden !important;
        transition: transform 0.8s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.8s cubic-bezier(0.25, 1, 0.5, 1), border-color 0.8s cubic-bezier(0.25, 1, 0.5, 1), background-color 0.8s cubic-bezier(0.25, 1, 0.5, 1) !important;
        will-change: transform, box-shadow, border-color;
        cursor: pointer !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.01), 0 1px 2px rgba(0, 0, 0, 0.005) !important;
        animation: cardSlideIn 0.8s cubic-bezier(0.25, 1, 0.5, 1) both;
      }
      .work-order-bento-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 4px;
        background-color: var(--accent-color);
        transition: width 0.5s cubic-bezier(0.25, 1, 0.5, 1);
        z-index: 2;
      }
      /* Premium glass sheen sweep sweep */
      .work-order-bento-card::after {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: linear-gradient(
          45deg,
          transparent 45%,
          ${darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.35)'} 50%,
          transparent 55%
        );
        transform: rotate(-45deg) translate(-75%, -75%);
        transition: transform 1.4s cubic-bezier(0.25, 1, 0.5, 1);
        pointer-events: none;
        z-index: 1;
      }
      .work-order-bento-card:hover {
        transform: translateY(-6px) !important;
        border-color: var(--accent-color) !important;
        box-shadow: 0 22px 30px -8px ${darkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(148, 163, 184, 0.12)'}, 0 8px 12px -6px ${darkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(148, 163, 184, 0.12)'}, 0 0 18px var(--accent-glow) !important;
      }
      .work-order-bento-card:hover::before {
        width: 6px;
      }
      .work-order-bento-card:hover::after {
        transform: rotate(-45deg) translate(75%, 75%);
      }
      .work-order-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: var(--badge-bg) !important;
        color: var(--accent-color) !important;
        border: 1px solid var(--accent-color) !important;
        border-radius: 20px;
        padding: 4px 12px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        transition: all 0.8s cubic-bezier(0.25, 1, 0.5, 1) !important;
      }
      .work-order-bento-card:hover .work-order-badge {
        box-shadow: 0 2px 8px var(--accent-glow);
        transform: scale(1.03);
      }
      .issued-search-field {
        transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1) !important;
      }
      .issued-search-field:focus, .issued-search-field:hover {
        border-color: ${themeStyles.colorPrimary || '#1890ff'} !important;
        box-shadow: 0 0 0 2px ${darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(7, 36, 60, 0.05)'} !important;
      }
    `;
  }, [customCardBg, customCardBorder, customTextMain, themeStyles, darkMode]);

  return (
    <div style={{ padding: '32px 24px', minHeight: '90vh', backgroundColor: customLayoutBg, transition: 'background-color 0.3s ease' }}>

      {/* HEADER BANNER */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        marginBottom: '36px',
        borderBottom: `2px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
        paddingBottom: '28px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '800',
              margin: '0 0 6px 0',
              letterSpacing: '-0.02em',
              color: customTextMain,
            }}>
              Issued Work Orders
            </h1>
            <p style={{
              fontSize: '15px',
              margin: 0,
              color: customTextSub
            }}>
              Review performa invoices issued for production and launch real-time timber and packed tracking channels.
            </p>
          </div>

          {/* Search Console Input */}
          {!loading && invoices.length > 0 && (
            <Input
              size="large"
              className="issued-search-field"
              placeholder="Search by PI # or client company..."
              prefix={<Icons.SearchOutlined style={{ color: customTextSub }} />}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{
                width: '320px',
                borderRadius: '6px',
                border: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)'}`,
                background: customCardBg,
                color: customTextMain,
                fontSize: '13px'
              }}
            />
          )}
        </div>
      </div>

      {/* CORE CONTENT */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh', width: '100%' }}>
          <Spin size="large" />
        </div>
      ) : filteredInvoices.length > 0 ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '24px',
            width: '100%',
          }}
        >
          {filteredInvoices.map((inv, index) => {
            const priority = getPriorityTheme(inv.pi_date, inv.pi_lead_time);
            const deadlineDate = dayjs(inv.pi_date).add(inv.pi_lead_time, 'day');

            return (
              <div
                key={inv.pi_id}
                className="work-order-bento-card"
                onClick={() => navigate(`/track-pi/${inv.pi_id}`)}
                style={{
                  '--accent-color': priority.color,
                  '--accent-glow': priority.glow,
                  '--badge-bg': priority.bg,
                  animationDelay: `${index * 45}ms`
                } as React.CSSProperties}
              >
                {/* Top Half: Order ID & Dynamic Time Dot */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                  <div>
                    <h3 style={{
                      fontSize: '22px',
                      fontWeight: '800',
                      margin: '0 0 2px 0',
                      color: customTextMain,
                      letterSpacing: '-0.02em'
                    }}>
                      #{inv.pi_order_no}
                    </h3>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: customTextSub, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                      PI-{inv.pi_id}
                    </span>
                  </div>

                  {/* Status Badge */}
                  <div className="work-order-badge">
                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: priority.color, display: 'inline-block' }} />
                    {priority.text}
                  </div>
                </div>

                {/* Divider Line */}
                <div style={{ borderTop: `1px dashed ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`, margin: '14px 0', width: '100%' }} />

                {/* Bottom Half: Details */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%', zIndex: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: customTextMain, fontSize: '13px', fontWeight: '600' }}>
                    <Icons.HomeOutlined style={{ color: customTextSub }} />
                    <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '90%' }}>
                      {inv.ledger_company_name || `${inv.ledger_first_name} ${inv.ledger_last_name}`}
                    </span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: customTextSub, fontSize: '12px' }}>
                      <Icons.AppstoreOutlined />
                      <span>{inv.items?.length || 0} production item{inv.items?.length !== 1 ? 's' : ''}</span>
                    </div>

                    <div style={{ fontSize: '12px', color: customTextSub, fontWeight: '500' }}>
                      Deadline: <strong style={{ color: customTextMain }}>{deadlineDate.format('DD MMM YYYY')}</strong>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* EMPTY STATE */
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh', width: '100%' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: customTextSub, fontSize: '15px' }}>
                {invoices.length > 0
                  ? "No issued work orders match your search parameters."
                  : "No active performa invoices registered in the system."}
              </span>
            }
          />
        </div>
      )}
    </div>
  );
};

export default IssuedWorkOrder;
